import React, { useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { TransactionCurrencyType } from 'common/api/autogenerated/client-area';

import { Button, DepositButton } from 'common/components';

import { useI18n, usePopup } from 'common/hooks';

import { routes } from 'routes/routes.constants';

import Web3 from '../..';
import { useWeb3 } from '../../hooks/useWeb3.hook';
import './index.css';

interface Web3ConnectSuccessPopupProps {
  action?: 'deposit' | 'token-presale-proceed';
  titleKey?: string;
  descriptionKey?: string;
}

export const Web3ConnectSuccessPopup: React.FC<Web3ConnectSuccessPopupProps> = ({
  action,
  titleKey = 'mobile:wallets:web3:connected:title',
  descriptionKey = 'mobile:wallets:web3:connected:subtitle',
}) => {
  const { token } = useWeb3();
  const { wallet } = useWeb3();
  const { translate } = useI18n();
  const { hide } = usePopup();

  const isDepositRoute = useRouteMatch(routes.getReceivePage());

  const forceTokenPresaleComplete = useMemo(() => action === 'token-presale-proceed', [action]);
  const forceDeposit = useMemo(
    () => !forceTokenPresaleComplete && (!isDepositRoute || action === 'deposit'),
    [action, forceTokenPresaleComplete, isDepositRoute],
  );

  const onClick = useCallback(() => {
    if (action === 'token-presale-proceed') {
      window.postMessage({ type: 'web3:signin:completed' }, '*');
      hide();
    }
  }, [action, hide]);

  return (
    <div className='web3-connect-success'>
      <img src={wallet?.icon} className='web3-connect-success__icon' alt='wallet-icon' />
      <p className='web3-connect-success__title'>
        {translate.toText(titleKey).replace('{wallet}', wallet?.name ?? '')} 🎉
      </p>
      <p className='web3-connect-success__description'>{translate.toText(descriptionKey)}</p>

      {!forceTokenPresaleComplete && (
        <div className='web3-connect-success__body'>
          <Web3.Balance />
          <Web3.Address icon={false} />
        </div>
      )}

      {forceTokenPresaleComplete && (
        <Button large className='web3-connect-success__button' onClick={onClick}>
          {translate('mobile:deposit:web3:button:proceed')}
        </Button>
      )}

      {forceDeposit && (
        <DepositButton
          large
          className='web3-connect-success__button'
          color='green'
          type={TransactionCurrencyType.CRYPTO}
          currency={token}
        >
          {translate.toText('mobile:wallets:web3:connected:button')}
        </DepositButton>
      )}
    </div>
  );
};

export const Web3ConnectSuccessPopupDefault = () => ({
  title: undefined,
  component: <Web3ConnectSuccessPopup />,
  useBottomSheet: true,
});

export const Web3ConnectSuccessPopupForPresale = () => ({
  ...Web3ConnectSuccessPopupDefault(),
  component: (
    <Web3ConnectSuccessPopup
      action='token-presale-proceed'
      descriptionKey='mobile:token-presale:web3:connected:subtitle'
    />
  ),
  onCloseHandler: () => window.postMessage({ type: 'web3:signin:completed' }, '*'),
  hideCloseIcon: true,
  wrapperClassName: 'web3-connect-success-wrapper',
});

export const Web3TokenPresaleConnectSuccessPopup = () => (
  <Web3ConnectSuccessPopup
    action='token-presale-proceed'
    descriptionKey='mobile:token-presale:web3:connected:subtitle'
  />
);
